/**
 * Undo per step (2.J deliverable 16): the Applied list and its Undo button. A click sends exactly
 * one request (the button is busy while it is in flight, and a ref refuses a second click before
 * the render); success reads "Undone."; a refusal or no answer reads 2.L's text. Nothing is
 * retried.
 */
import { undoApply } from '@codex/contracts';
import { Button } from '@codex/ui';
import { useEffect, useId, useRef, useState } from 'react';
import { api } from '../auth/client.js';
import { NOTICE_MS } from '../shell/notices.js';
import { formatWhen } from '../trash/format.js';
import { type AppliedRow, refusalText, UNDONE_TEXT } from './model/step-undo.js';

export function UndoButton({
  run,
  disabledReason,
  text,
  setText,
}: {
  run: () => Promise<void>;
  disabledReason: string | null;
  /** The confirmation or refusal text; owned by the list so it outlives this button. */
  text: string | null;
  setText: (text: string | null, keepFor?: number) => void;
}) {
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const reasonId = useId();
  const click = async () => {
    if (inFlight.current || disabledReason !== null) return;
    inFlight.current = true;
    setBusy(true);
    try {
      await run();
      setText(UNDONE_TEXT, NOTICE_MS);
    } catch (error) {
      setText(refusalText(error));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  return (
    <span className="cx-bootstrap__undo">
      <Button
        size="sm"
        icon="undo"
        data-testid="bootstrap.undo.button"
        busy={busy}
        disabled={disabledReason !== null}
        aria-describedby={disabledReason === null ? undefined : reasonId}
        onClick={() => void click()}
      >
        Undo
      </Button>
      {disabledReason !== null && (
        <span id={reasonId} className="cx-bootstrap__muted">
          {disabledReason}
        </span>
      )}
      <span role="status" className="cx-bootstrap__muted">
        {text}
      </span>
    </span>
  );
}

/** Reverts one change batch through `undo.apply`. */
export async function undoBatch(id: string): Promise<void> {
  await api(undoApply, { params: { id } });
}

export function AppliedList({
  rows,
  undo,
  onUndone,
}: {
  rows: readonly AppliedRow[];
  undo: (row: AppliedRow) => Promise<void>;
  onUndone: () => void;
}) {
  const [texts, setTexts] = useState<Readonly<Record<string, string>>>({});
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  useEffect(() => {
    const live = timers.current;
    return () => {
      for (const t of live.values()) clearTimeout(t);
      live.clear();
    };
  }, []);
  /** Keeps a row's status; with `keepFor` it clears itself after that many ms. */
  const setText = (id: string, text: string | null, keepFor?: number) => {
    const old = timers.current.get(id);
    if (old !== undefined) clearTimeout(old);
    timers.current.delete(id);
    setTexts((prev) => {
      const { [id]: _drop, ...rest } = prev;
      return text === null ? rest : { ...rest, [id]: text };
    });
    if (text !== null && keepFor !== undefined) {
      timers.current.set(
        id,
        setTimeout(() => setText(id, null), keepFor),
      );
    }
  };
  if (rows.length === 0) return null;
  return (
    <section aria-label="Applied" className="cx-bootstrap__applied">
      <h3>Applied</h3>
      <ul>
        {rows.map((row) => (
          <li key={row.id}>
            <span>
              {row.summary}
              {row.time === '' ? '' : `, ${formatWhen(row.time)}`}
            </span>
            {row.undone ? (
              <>
                <span className="cx-bootstrap__muted">Undone</span>
                {texts[row.id] !== undefined && (
                  <span role="status" className="cx-bootstrap__muted">
                    {texts[row.id]}
                  </span>
                )}
              </>
            ) : (
              <UndoButton
                disabledReason={row.disabledReason}
                text={texts[row.id] ?? null}
                setText={(text, keepFor) => setText(row.id, text, keepFor)}
                run={async () => {
                  await undo(row);
                  onUndone();
                }}
              />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
